import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  type CustomFieldDefinition,
  type CustomFieldListOption,
} from 'src/api/generated';
import {
  useCustomFieldListOptions,
  useCreateCustomFieldListOption,
  useUpdateCustomFieldListOption,
  useDeleteCustomFieldListOption,
} from 'src/modules/shared/hooks/api/custom-fields/list-options';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import { FormField } from 'src/modules/shared/components/molecules/form-field';
import Loading from 'src/modules/shared/components/atoms/loading';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import { Badge } from 'src/modules/shared/components/atoms/badge';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { PiPencil, PiTrash, PiInfo, PiListBullets } from 'react-icons/pi';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { CrmCustomFieldPermission } from 'src/modules/shared/types/permissions.type';

const listOptionSchema = z.object({
  value: z.string().min(1, 'El valor es requerido'),
  label: z.string().optional(),
  description: z.string().optional(),
});

type ListOptionFormData = z.infer<typeof listOptionSchema>;

interface CustomFieldListOptionsManagerProps {
  definition: CustomFieldDefinition;
}

export interface CustomFieldListOptionsManagerRef {
  handleCreateNew: () => void;
}

export const CustomFieldListOptionsManager = forwardRef<
  CustomFieldListOptionsManagerRef,
  CustomFieldListOptionsManagerProps
>(({ definition }, ref) => {
  const [showForm, setShowForm] = useState(false);
  const [editingOption, setEditingOption] =
    useState<CustomFieldListOption | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] =
    useState<CustomFieldListOption | null>(null);

  const { hasPermission: canCreate } = useHasPermissions(
    CrmCustomFieldPermission.Create
  );
  const { hasPermission: canUpdate } = useHasPermissions(
    CrmCustomFieldPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    CrmCustomFieldPermission.Delete
  );

  const handleCreateNew = () => {
    if (!canCreate) return;

    reset();
    setEditingOption(null);
    setShowForm(true);
  };

  useImperativeHandle(ref, () => ({
    handleCreateNew,
  }));

  const optionsQuery = useMemo(() => {
    return createQuery()
      .equals('definitionId', definition.id)
      .orderBy('createdAt', 'asc')
      .build();
  }, [definition.id]);

  const { options, loading, error, refetch } =
    useCustomFieldListOptions(optionsQuery);

  const { createOption, loading: creating } = useCreateCustomFieldListOption({
    onSuccess: () => {
      setShowForm(false);
      setEditingOption(null);
    },
  });

  const { updateOption, loading: updating } = useUpdateCustomFieldListOption({
    onSuccess: () => {
      setShowForm(false);
      setEditingOption(null);
    },
  });

  const { deleteOption, loading: deleting } = useDeleteCustomFieldListOption({
    onSuccess: () => {
      setDeleteModalOpen(false);
      setOptionToDelete(null);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
  } = useForm<ListOptionFormData>({
    resolver: zodResolver(listOptionSchema),
    mode: 'onChange',
  });

  const handleEdit = (option: CustomFieldListOption) => {
    if (!canUpdate) return;

    setEditingOption(option);
    setValue('value', option.value);
    setValue('label', option.label ?? '');
    setValue('description', option.description ?? '');
    setShowForm(true);
  };

  const handleDeleteClick = (option: CustomFieldListOption) => {
    if (!canDelete) return;

    setOptionToDelete(option);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!optionToDelete) return;

    await deleteOption({
      definitionId: definition.id,
      optionId: optionToDelete.id,
    });
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setOptionToDelete(null);
  };

  const onSubmit = async (data: ListOptionFormData) => {
    try {
      if (editingOption) {
        await updateOption({
          definitionId: definition.id,
          optionId: editingOption.id,
          data: {
            label: data.label,
            description: data.description,
          },
        });
      } else {
        await createOption({
          definitionId: definition.id,
          value: data.value,
          label: data.label,
          description: data.description,
        });
      }
    } catch (error) {
      console.error('Error saving list option:', error);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingOption(null);
    reset();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        // TODO: check when implementing i18n
        title="Error cargando opciones"
        // TODO: check when implementing i18n
        description="No se pudieron cargar las opciones del campo. Por favor, inténtalo de nuevo."
        onAction={() => refetch()}
        // TODO: check when implementing i18n
        actionContent="Reintentar"
      />
    );
  }

  return (
    <div className="space-y-6">
      {showForm && (
        <Card variant="neutral" appearance="outline" className="p-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Text size="paragraph" weight="medium">
              {/* TODO: check when implementing i18n */}
              {editingOption ? 'Editar Opción' : 'Nueva Opción'}
            </Text>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                // TODO: check when implementing i18n
                label="Valor"
                error={errors.value?.message}
                // TODO: check when implementing i18n
                required
                helpText="Valor único que se guardará en la base de datos"
              >
                <TextInput
                  {...register('value')}
                  // TODO: check when implementing i18n
                  placeholder="ej: website"
                  disabled={!!editingOption}
                />
              </FormField>

              <FormField
                // TODO: check when implementing i18n
                label="Etiqueta"
                error={errors.label?.message}
                // TODO: check when implementing i18n
                helpText="Texto que se mostrará al usuario"
              >
                {/* TODO: check when implementing i18n */}
                <TextInput {...register('label')} placeholder="ej: Sitio Web" />
              </FormField>
            </div>

            <FormField
              // TODO: check when implementing i18n
              label="Descripción"
              error={errors.description?.message}
              // TODO: check when implementing i18n
              helpText="Descripción opcional de esta opción"
            >
              <TextArea
                {...register('description')}
                // TODO: check when implementing i18n
                placeholder="Describe cuándo usar esta opción..."
                rows={2}
              />
            </FormField>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                onClick={handleCancelForm}
                disabled={creating || updating}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={creating || updating}
                disabled={!isValid}
              >
                {editingOption ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {options.length === 0 && !showForm ? (
        <EmptyState
          title="No hay opciones configuradas"
          description="Agrega opciones para que los usuarios puedan seleccionar valores para este campo."
          icon={<PiListBullets className="w-full h-full" />}
          placement="vertical"
        />
      ) : (
        <div className="space-y-3">
          {options.map((option) => (
            <Card
              key={option.id}
              variant="neutral"
              appearance="outline"
              className="relative overflow-visible p-3"
            >
              <div className="flex flex-col h-full mt-1">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Text
                      size="small"
                      weight="medium"
                      className="text-gray-900"
                    >
                      {nonEmptyString(option.label) ?? option.value}
                    </Text>
                    <Badge variant="neutral" size="sm">
                      {option.value}
                    </Badge>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-0.5">
                      {option.description && (
                        <Text size="tiny" className="text-secondary-700">
                          {option.description}
                        </Text>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canUpdate && (
                        <Button
                          variant="neutral"
                          appearance="ghost"
                          size="sm"
                          onClick={() => handleEdit(option)}
                          className="flex items-center gap-1"
                        >
                          <PiPencil className="w-4 h-4" />
                          <Text size="small">Editar</Text>
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="neutral"
                          appearance="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(option)}
                          className="flex items-center gap-1 text-danger-600 hover:text-danger-700"
                        >
                          <PiTrash className="w-4 h-4" />
                          <Text size="small">Eliminar</Text>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {deleteModalOpen && optionToDelete && (
        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={handleCancelDelete}
          // TODO: check when implementing i18n
          title="Eliminar Opción"
          size="sm"
          onConfirm={handleConfirmDelete}
          // TODO: check when implementing i18n
          confirmText="Eliminar"
          // TODO: check when implementing i18n
          cancelText="Cancelar"
          confirmDisabled={deleting}
          loading={deleting}
          variant="danger"
        >
          <div className="flex items-start gap-3">
            <PiInfo className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <Text size="small" className="text-gray-700">
              {/* TODO: check when implementing i18n */}
              ¿Estás seguro de que quieres eliminar la opción{' '}
              <span className="font-medium">
                {nonEmptyString(optionToDelete.label) ?? optionToDelete.value}
              </span>
              ?
            </Text>
          </div>
        </ConfirmModal>
      )}
    </div>
  );
});

CustomFieldListOptionsManager.displayName = 'CustomFieldListOptionsManager';
